/**
 * Plain-text version of a rendered Pebbl reply, for the `text/plain` flavour of a
 * copy: what a target with no rich-text support (a plain textarea, a terminal, a
 * chat app) pastes.
 *
 * Built by walking the rendered HTML rather than from the Markdown, so it is the
 * reply as read - no `**`, no `|` table rules - whatever syntax produced it. A
 * list keeps its `- ` / `1.` markers, blocks are separated by a blank line, and
 * table cells are tab-separated so a table pasted into a spreadsheet lands one
 * value per cell.
 *
 * Parsed into an inert `<template>`, so nothing in the HTML loads or runs.
 */
export function htmlToPlainText(html: string): string {
  const template = document.createElement('template');
  template.innerHTML = html || '';
  return blocksOf(template.content)
    .join('\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Stands in for a `<br>` until whitespace is collapsed, so the source HTML's own
 *  newlines (formatting only) can be squeezed without losing the real breaks. */
const BREAK = ' ';

const BLOCK_TAGS = new Set([
  'ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'DD', 'DIV', 'DL', 'DT', 'FIGURE', 'FOOTER',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'HEADER', 'HR', 'LI', 'OL', 'P', 'PRE', 'SECTION',
  'TABLE', 'TBODY', 'TD', 'TFOOT', 'TH', 'THEAD', 'TR', 'UL',
]);

function isBlock(el: HTMLElement): boolean {
  return BLOCK_TAGS.has(el.tagName);
}

/** A container's block-level pieces in reading order; a run of inline content
 *  between two blocks counts as one piece. */
function blocksOf(parent: ParentNode): string[] {
  const out: string[] = [];
  let inline = '';
  const flush = (): void => {
    const text = tidy(inline);
    if (text) out.push(text);
    inline = '';
  };
  parent.childNodes.forEach((node) => {
    if (node instanceof HTMLElement && isBlock(node)) {
      flush();
      const text = blockText(node);
      if (text) out.push(text);
    } else {
      inline += inlineText(node);
    }
  });
  flush();
  return out;
}

function blockText(el: HTMLElement): string {
  switch (el.tagName) {
    case 'UL':
    case 'OL':
      return listText(el, 0);
    case 'TABLE':
      return tableText(el);
    case 'PRE':
      // Code keeps its own spacing and line breaks.
      return (el.textContent ?? '').replace(/\n+$/, '');
    case 'HR':
      return '----';
    default:
      return blocksOf(el).join('\n\n');
  }
}

function inlineText(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? '';
  }
  if (!(node instanceof HTMLElement)) {
    return '';
  }
  if (node.tagName === 'BR') {
    return BREAK;
  }
  if (isBlock(node)) {
    return BREAK + blockText(node).replace(/\n/g, BREAK) + BREAK;
  }
  const text = Array.from(node.childNodes).map(inlineText).join('');
  // A link's address is part of the message; without it a plain-text paste
  // keeps only the words. Skipped when the words already are the address.
  if (node.tagName === 'A') {
    const href = (node.getAttribute('href') || '').trim();
    const shown = href.replace(/^mailto:/i, '');
    if (/^(https?:|mailto:)/i.test(href) && text.trim() && text.trim() !== shown) {
      return `${text} (${shown})`;
    }
  }
  return text;
}

function listText(list: HTMLElement, depth: number): string {
  const ordered = list.tagName === 'OL';
  let number = Number(list.getAttribute('start')) || 1;
  const indent = '  '.repeat(depth);
  const lines: string[] = [];
  Array.from(list.children).forEach((item) => {
    if (item.tagName !== 'LI') return;
    const marker = ordered ? `${number++}.` : '-';
    let own = '';
    const nested: string[] = [];
    item.childNodes.forEach((child) => {
      if (child instanceof HTMLElement && (child.tagName === 'UL' || child.tagName === 'OL')) {
        nested.push(listText(child, depth + 1));
      } else {
        own += inlineText(child);
      }
    });
    // A wrapped item's later lines line up under its first word, not its marker.
    const pad = `${indent}${' '.repeat(marker.length + 1)}`;
    lines.push(`${indent}${marker} ${tidy(own).replace(/\n/g, `\n${pad}`)}`);
    lines.push(...nested);
  });
  return lines.join('\n');
}

function tableText(table: HTMLElement): string {
  return Array.from(table.querySelectorAll('tr'))
    .map((row) =>
      Array.from(row.children)
        .filter((cell) => cell.tagName === 'TD' || cell.tagName === 'TH')
        .map((cell) => tidy(inlineText(cell)).replace(/\n/g, ' '))
        .join('\t'),
    )
    .filter((row) => row.replace(/\t/g, '').trim() !== '')
    .join('\n');
}

/** Squeeze the HTML source's formatting whitespace to single spaces, then turn
 *  the `<br>` stand-ins back into line breaks. */
function tidy(text: string): string {
  return text
    .replace(/[ \t\r\n\f]+/g, ' ')
    .split(BREAK)
    .map((line) => line.trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
